import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import api from 'api';
import { AssistantType } from 'api/resources/accounts/types';
import useAgentWizardActiveJob from './useAgentWizardActiveJob';
import { useIsFeatureOn } from './useFeatureSwitch';
import useProjectRoute from './useProjectRoute';
import useSampleProject from './useSampleProjects/useSampleProject';

export const generateProjectsQueryKey = (accountId: string) => [
  accountId,
  'projects',
];

const useProjects = () => {
  const { accountId } = useProjectRoute();
  const { inSampleProject } = useSampleProject();
  const { isFeatureOn } = useIsFeatureOn();
  const isPortalProjectsFeatureOn = isFeatureOn('portal_projects_in_jupiter');

  const {
    data,
    isLoading: isLoadingProjects,
    isError: isProjectsError,
    refetch: refetchProjects,
  } = useQuery({
    queryKey: generateProjectsQueryKey(accountId),
    queryFn: () => api.getProjects(accountId),
    enabled: !inSampleProject && !!accountId,
  });

  const { activeJob, isLoadingActiveJob } = useAgentWizardActiveJob();

  const projects = useMemo(() => {
    const allProjects = data?.projects ?? [];

    const filteredProjects = isPortalProjectsFeatureOn
      ? allProjects
      : allProjects.filter(project => project.type !== AssistantType.INTENT);

    // Exclude wizard setup in-progress projects. no-op outside PLG
    return filteredProjects.map(project =>
      activeJob?.projectId === project.id
        ? { ...project, active: false }
        : project,
    );
  }, [data?.projects, isPortalProjectsFeatureOn, activeJob?.projectId]);

  const projectIdToNameMap = useMemo(() => {
    const idToNameMap = {} as { [key: string]: string };

    projects.forEach(project => (idToNameMap[project.id] = project.name ?? ''));

    return idToNameMap;
  }, [projects]);

  return {
    projects,
    isLoadingProjects: isLoadingProjects || isLoadingActiveJob,
    isProjectsError,
    projectIdToNameMap,
    refetchProjects,
  };
};

export default useProjects;
